const range = (start, end) => Array.from({ length: end - start + 1 }, (_, index) => start + index);

function lesson({ id, tutorialId, name, outcome, stepNumbers = null, minutes = null }) {
  return {
    id,
    tutorialId,
    name,
    outcome,
    stepIndexes: stepNumbers?.map((stepNumber) => stepNumber - 1) || null,
    minutes: minutes || Math.max(2, Math.ceil((stepNumbers?.length || 8) * 0.4)),
  };
}

const QUICK_START = {
  admin: [
    lesson({ id: "branch-setup", tutorialId: "branch-setup", name: "Set up the clinic", outcome: "Confirm clinic details, opening hours, booking defaults, and treatment chairs.", minutes: 5 }),
    lesson({ id: "services-pricing", tutorialId: "services-pricing", name: "Set services and prices", outcome: "Review clinic fees, appointment time, and built-in treatment setup.", minutes: 4 }),
    lesson({ id: "doctor-setup", tutorialId: "doctor-setup", name: "Add your team", outcome: "Invite staff, choose roles, and review dentist commission.", minutes: 5 }),
  ],
  doctor: [
    lesson({ id: "initial-charting-upper-right", tutorialId: "initial-charting", name: "Initial charting: start the exam", outcome: "Check in, choose the charting tools, and record the first findings.", stepNumbers: range(1, 12), minutes: 5 }),
    lesson({ id: "doctor-build-plan", tutorialId: "tx-planning", name: "Build a treatment plan", outcome: "Turn findings into an estimate and visit sequence.", stepNumbers: range(1, 10), minutes: 4 }),
    lesson({ id: "multi-visit-start", tutorialId: "ongoing-treatment", name: "Start multi-visit treatment", outcome: "Record the first visit without closing the treatment.", stepNumbers: range(1, 11), minutes: 5 }),
  ],
  "front-desk": [
    lesson({ id: "patient-registration", tutorialId: "patient-registration", name: "Register a patient", outcome: "Practice searching first and entering a clean patient record.", minutes: 4 }),
    lesson({ id: "front-desk-book-appointment", tutorialId: "booking-calendar", name: "Book an appointment", outcome: "Practice the full booking without changing the clinic calendar.", minutes: 5 }),
    lesson({ id: "front-desk-checkout", tutorialId: "front-desk-routine-checkout", name: "Complete a routine checkout", outcome: "Review the dentist's work, collect, book the return, and finish.", minutes: 5 }),
  ],
  assistant: [
    lesson({ id: "assistant-patient-overview", tutorialId: "patient-overview", name: "Find the patient record", outcome: "Open the chart, history, files, and treatment information.", stepNumbers: range(1, 10), minutes: 4 }),
    lesson({ id: "patient-registration", tutorialId: "patient-registration", name: "Help register a patient", outcome: "Search first and avoid duplicate patient records.", minutes: 4 }),
    lesson({ id: "assistant-booking-basics", tutorialId: "booking-calendar", name: "Understand the appointment", outcome: "See how the clinic chooses the patient, chair, and time.", stepNumbers: range(1, 10), minutes: 4 }),
  ],
};

const ADVANCED = {
  admin: [
    lesson({ id: "branch-management", tutorialId: "branch-management", name: "Manage another location", outcome: "Review branch status and understand what happens before adding another clinic location.", minutes: 2 }),
    lesson({ id: "treatment-pipeline", tutorialId: "treatment-pipeline", name: "Owner view: treatment plans", outcome: "See proposed treatment value and follow-up risk after the clinic starts using Dental OS.", minutes: 2 }),
  ],
  doctor: [
    lesson({ id: "initial-charting-upper-left", tutorialId: "initial-charting", name: "Initial charting: upper-left findings", outcome: "Record caries, a failing restoration, and tooth-specific detail.", stepNumbers: range(13, 23), minutes: 5 }),
    lesson({ id: "initial-charting-lower", tutorialId: "initial-charting", name: "Initial charting: lower teeth", outcome: "Finish the lower findings, review the chart, and save.", stepNumbers: range(24, 35), minutes: 5 }),
    lesson({ id: "doctor-today-visit", tutorialId: "today", name: "Today: record performed treatment", outcome: "Record chairside work and hand it to Front Desk.", stepNumbers: range(1, 10), minutes: 4 }),
    lesson({ id: "future-need-record", tutorialId: "future-need", name: "Future Need: record the treatment", outcome: "Keep diagnosed work visible and hand it to Front Desk.", stepNumbers: range(1, 8), minutes: 4 }),
    lesson({ id: "rct-zirconia-plan", tutorialId: "rct-zirconia-multi-visit", name: "Tx Plan RCT and Zirconia: build the case", outcome: "Arrange RCT and crown work across appointments.", stepNumbers: range(1, 11), minutes: 5 }),
    lesson({ id: "rct-zirconia-propose", tutorialId: "rct-zirconia-multi-visit", name: "Tx Plan RCT and Zirconia: propose it", outcome: "Review the visits, estimate, and patient handoff.", stepNumbers: range(12, 22), minutes: 5 }),
    lesson({ id: "multi-visit-return", tutorialId: "ongoing-treatment", name: "Multi-visit: record a return visit", outcome: "Record visit two and confirm that ₱0 is due on a 50/50 plan.", stepNumbers: range(27, 37), minutes: 5 }),
    lesson({ id: "multi-visit-finish", tutorialId: "ongoing-treatment", name: "Multi-visit: finish and collect", outcome: "Close treatment clinically and collect the final balance.", stepNumbers: [38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 50, 51], minutes: 5 }),
    lesson({ id: "doctor-ortho-bonding", tutorialId: "doctor-ortho", name: "Chairside Ortho: bonding", outcome: "Record the installation visit and next-visit plan.", stepNumbers: range(1, 7), minutes: 4 }),
    lesson({ id: "doctor-ortho-adjustment", tutorialId: "doctor-ortho", name: "Chairside Ortho: adjustment", outcome: "Record wires, elastics, hygiene, notes, and the next visit.", stepNumbers: range(8, 18), minutes: 5 }),
    lesson({ id: "doctor-ortho-debonding", tutorialId: "doctor-ortho", name: "Chairside Ortho: debonding", outcome: "Open the removal visit, record retainers, and finish the case event.", stepNumbers: range(19, 23), minutes: 3 }),
    lesson({ id: "patient-overview", tutorialId: "patient-overview", name: "Patient overview", outcome: "Review the complete patient record.", minutes: 4 }),
  ],
  "front-desk": [
    lesson({ id: "checkout-add-procedures", tutorialId: "checkout-add-procedures", name: "Fix a missing chairside item", outcome: "Use only when the dentist confirms completed work is missing.", minutes: 5 }),
    lesson({ id: "tx-plan-checkout", tutorialId: "checkout-tx-planning", name: "Treatment plan: collect and book", outcome: "Choose how the patient will pay, collect today, and book the next visit.", stepNumbers: [2, 4, 5, 7, 10, 11, 12, 15, 16, 17, 18, 20], minutes: 5 }),
    lesson({ id: "future-need-book", tutorialId: "future-need", name: "Future Need: patient books", outcome: "Choose a slot when the patient is ready to return.", stepNumbers: [9, 10, 11, 12], minutes: 2 }),
    lesson({ id: "future-need-recall", tutorialId: "future-need", name: "Future Need: follow up later", outcome: "Send the treatment to Recall when the patient cannot book yet.", stepNumbers: [9, 13, 14], minutes: 2 }),
    lesson({ id: "future-need-decline", tutorialId: "future-need", name: "Future Need: patient declines", outcome: "Record a clear refusal so staff do not keep calling.", stepNumbers: [9, 15, 16], minutes: 2 }),
    lesson({ id: "hmo-enter-coverage", tutorialId: "hmo-checkout-claims", name: "HMO: enter coverage", outcome: "Record the LOA, covered amount, and patient share.", stepNumbers: range(1, 12), minutes: 5 }),
    lesson({ id: "hmo-finish-checkout", tutorialId: "hmo-checkout-claims", name: "HMO: finish checkout", outcome: "Book the return and finish the HMO handoff.", stepNumbers: range(13, 18), minutes: 3 }),
    lesson({ id: "hmo-collect-claim", tutorialId: "hmo-checkout-claims", name: "HMO: submit and collect", outcome: "Submit a claim and record full or partial payment.", stepNumbers: range(19, 30), minutes: 5 }),
    lesson({ id: "accepted-set-payment", tutorialId: "accepted-not-paid-today", name: "Choose how the patient will pay", outcome: "Set the agreed payment schedule for accepted care.", stepNumbers: range(1, 8), minutes: 4 }),
    lesson({ id: "accepted-record-payment", tutorialId: "accepted-not-paid-today", name: "Record a partial payment", outcome: "Record what the patient actually gives today and keep the balance visible.", stepNumbers: [9, 10, 11, 13, 14], minutes: 3 }),
    lesson({ id: "recall-book-patient", tutorialId: "recall-flow", name: "Recall: call and book", outcome: "Open one patient, call, and confirm the appointment they chose.", stepNumbers: [1, 6, 7, 8, 10, 12, 13, 15, 17, 18, 19, 20], minutes: 5 }),
    lesson({ id: "recall-no-answer", tutorialId: "recall-flow", name: "Recall: record no answer", outcome: "Pause outreach without treating silence as a decline.", stepNumbers: range(21, 27), minutes: 3 }),
    lesson({ id: "recall-decline", tutorialId: "recall-flow", name: "Recall: record a decline", outcome: "Save the patient’s clear decision and reason.", stepNumbers: range(28, 35), minutes: 4 }),
    lesson({ id: "ar-center", tutorialId: "ar-center", name: "Collect unpaid balances", outcome: "Review completed visits that still need payment.", minutes: 2 }),
    lesson({ id: "ortho-transfer-open", tutorialId: "ortho-transfer-case", name: "Ortho transfer: open the import", outcome: "Choose the patient and the old billing arrangement.", stepNumbers: range(1, 9), minutes: 4 }),
    lesson({ id: "ortho-transfer-owner", tutorialId: "ortho-transfer-case", name: "Ortho transfer: case and dentist", outcome: "Record the appliance, responsible dentist, and current status.", stepNumbers: range(10, 16), minutes: 3 }),
    lesson({ id: "ortho-transfer-money", tutorialId: "ortho-transfer-case", name: "Ortho transfer: prior payments", outcome: "Record the old agreement without creating new clinic income.", stepNumbers: range(17, 25), minutes: 4 }),
    lesson({ id: "ortho-transfer-finish", tutorialId: "ortho-transfer-case", name: "Ortho transfer: notes and finish", outcome: "Document record quality, import, and prepare the next adjustment.", stepNumbers: range(26, 35), minutes: 4 }),
    lesson({ id: "ortho-module", tutorialId: "ortho-module", name: "Ortho Center", outcome: "Find active cases and factual follow-up needs.", minutes: 2 }),
  ],
  assistant: [
    lesson({ id: "assistant-patient-detail", tutorialId: "patient-overview", name: "Patient record", outcome: "Review the patient’s chart, history, and files.", minutes: 4 }),
    lesson({ id: "assistant-calendar", tutorialId: "booking-calendar", name: "Calendar and chair basics", outcome: "Understand where the patient, dentist, and chair are assigned.", stepNumbers: range(1, 10), minutes: 4 }),
  ],
};

const MORE_TRAINING_GROUPS = {
  admin: [
    {
      id: "clinic-management",
      label: "Clinic management",
      description: "Branches and owner-only clinic controls.",
      lessonIds: ["branch-management"],
    },
    {
      id: "owner-visibility",
      label: "Owner visibility",
      description: "See the treatment work and value moving through the clinic.",
      lessonIds: ["treatment-pipeline"],
    },
  ],
  doctor: [
    {
      id: "dynamic-charting",
      label: "Dynamic Charting",
      description: "Record findings, today’s work, and treatment that still needs attention.",
      lessonIds: [
        "initial-charting-upper-left",
        "initial-charting-lower",
        "doctor-today-visit",
        "future-need-record",
      ],
    },
    {
      id: "treatment-plans",
      label: "Treatment Plans",
      description: "Build and explain planned care across appointments.",
      lessonIds: ["rct-zirconia-plan", "rct-zirconia-propose"],
    },
    {
      id: "multi-visit-treatment",
      label: "Multi-Visit Treatment",
      description: "Continue return-visit care without closing it too early.",
      lessonIds: ["multi-visit-return", "multi-visit-finish"],
    },
    {
      id: "ortho",
      label: "Ortho",
      description: "Record bonding, adjustment, and debonding visits chairside.",
      lessonIds: ["doctor-ortho-bonding", "doctor-ortho-adjustment", "doctor-ortho-debonding"],
    },
    {
      id: "patient-records",
      label: "Patient Records",
      description: "Review the patient’s complete clinical and payment history.",
      lessonIds: ["patient-overview"],
    },
  ],
  "front-desk": [
    {
      id: "checkout-payments",
      label: "Checkout & Payments",
      description: "Review completed work and record what the patient pays today.",
      lessonIds: ["checkout-add-procedures", "accepted-set-payment", "accepted-record-payment"],
    },
    {
      id: "treatment-plans",
      label: "Treatment Plans",
      description: "Receive the dentist’s handoff, explain payment, and book the next visit.",
      lessonIds: [
        "tx-plan-checkout",
        "future-need-book",
        "future-need-recall",
        "future-need-decline",
      ],
    },
    {
      id: "hmo-claims",
      label: "HMO Claims",
      description: "Record coverage, finish checkout, and collect the provider claim.",
      lessonIds: ["hmo-enter-coverage", "hmo-finish-checkout", "hmo-collect-claim"],
    },
    {
      id: "recall",
      label: "Recall",
      description: "Work the follow-up queue and record what happened after each contact.",
      lessonIds: ["recall-book-patient", "recall-no-answer", "recall-decline"],
    },
    {
      id: "accounts-receivable",
      label: "Accounts Receivable",
      description: "Find completed visits that still have a real unpaid balance.",
      lessonIds: ["ar-center"],
    },
    {
      id: "ortho-operations",
      label: "Ortho Operations",
      description: "Take over transfer cases and keep active cases visible.",
      lessonIds: ["ortho-transfer-open", "ortho-transfer-owner", "ortho-transfer-money", "ortho-transfer-finish", "ortho-module"],
    },
  ],
  assistant: [
    {
      id: "patient-records",
      label: "Patient Records",
      description: "Find the chart, history, files, and treatment information.",
      lessonIds: ["assistant-patient-detail"],
    },
    {
      id: "calendar-chairside",
      label: "Calendar & Chairside Basics",
      description: "Understand the patient, dentist, chair, and appointment handoff.",
      lessonIds: ["assistant-calendar"],
    },
  ],
};

export const TUTORIAL_ROLE_TRACKS = {
  admin: {
    label: "Clinic Admin",
    description: "Set up the clinic and see what needs attention.",
    quickStart: QUICK_START.admin,
    advanced: ADVANCED.admin,
  },
  doctor: {
    label: "Dentist",
    description: "Start with the exam, then record care and keep treatment moving.",
    quickStart: QUICK_START.doctor,
    advanced: ADVANCED.doctor,
  },
  "front-desk": {
    label: "Front Desk",
    description: "Book, collect, and keep the patient’s next step clear.",
    quickStart: QUICK_START["front-desk"],
    advanced: ADVANCED["front-desk"],
  },
  assistant: {
    label: "Assistant",
    description: "Find the patient and understand today’s clinic handoff.",
    quickStart: QUICK_START.assistant,
    advanced: ADVANCED.assistant,
  },
};

export const TUTORIAL_MORE_TRAINING_GROUPS = Object.fromEntries(
  Object.entries(MORE_TRAINING_GROUPS).map(([roleKey, groups]) => {
    const lessonsById = new Map(
      TUTORIAL_ROLE_TRACKS[roleKey].advanced.map((lessonItem) => [lessonItem.id, lessonItem]),
    );
    return [
      roleKey,
      groups.map((group) => ({
        ...group,
        lessons: group.lessonIds.map((lessonId) => lessonsById.get(lessonId)).filter(Boolean),
      })),
    ];
  }),
);

export function tutorialRoleKey(clinicRole, isPlatformAdmin = false) {
  if (isPlatformAdmin || clinicRole === "clinic_admin") return "admin";
  if (clinicRole === "doctor") return "doctor";
  if (clinicRole === "assistant") return "assistant";
  return "front-desk";
}

export function lessonStepCount(lessonItem, tutorial) {
  return lessonItem.stepIndexes?.length || tutorial?.steps?.length || 0;
}
